import { createListItem, deleteListItem, getListItems } from '../graph/lists';
import { LISTS, READ_CACHE_MS } from './listNames';
import { buildAllowlist, EMPTY_ALLOWLIST, parseAllowEntry, storedEntry, type EmailAllowlist } from './recipients';

/**
 * Outside-email allowlist (owner rulings 2026-10-19; rule in recipients.ts):
 * one row per allowed outside domain or address, with a note. No expiry —
 * an admin removes the row. Read by the comment composer (everyone), the
 * Reports editor, the completion notice editor, the server and Flows 2c + 4.
 */

interface AllowFields {
  /** The domain (vendor.com) or address (jane@vendor.com), normalized. */
  Title: string;
  EntryNote: string;
  AddedBy: string;
}

export interface AllowEntry {
  id: string;
  entry: string;
  kind: 'domain' | 'address' | 'invalid';
  note: string;
  addedBy: string;
  addedAt: string;
}

export async function getAllowEntries(): Promise<AllowEntry[]> {
  const items = await getListItems<AllowFields>(LISTS.emailAllowlist, { cacheMs: READ_CACHE_MS });
  return items
    .map((i) => {
      const raw = i.fields.Title ?? '';
      const parsed = storedEntry(raw);
      return {
        id: i.id,
        // Rows not in stored form (typed straight into SharePoint) are shown so they can be removed — never used.
        entry: parsed ? parsed.value : raw,
        kind: parsed ? parsed.kind : ('invalid' as const),
        note: i.fields.EntryNote ?? '',
        addedBy: i.fields.AddedBy ?? '',
        addedAt: i.createdDateTime ?? '',
      };
    })
    .sort((a, b) => a.entry.localeCompare(b.entry));
}

/**
 * The usable allowlist. A failed read (e.g. the list isn't provisioned yet)
 * = company addresses only — never an error in front of the user.
 */
export async function getEmailAllowlist(): Promise<EmailAllowlist> {
  try {
    return buildAllowlist((await getAllowEntries()).map((e) => e.entry));
  } catch {
    return EMPTY_ALLOWLIST;
  }
}

/** Add one entry (validated here and again on the server); returns the stored form. */
export async function addAllowEntry(raw: string, note: string, addedBy: string): Promise<string> {
  const parsed = parseAllowEntry(raw);
  if ('problem' in parsed) throw new Error(parsed.problem);
  const existing = await getAllowEntries();
  if (existing.some((e) => e.kind !== 'invalid' && e.entry === parsed.value)) {
    throw new Error(`${parsed.value} is already on the list.`);
  }
  await createListItem<AllowFields>(LISTS.emailAllowlist, {
    Title: parsed.value,
    EntryNote: note.trim().slice(0, 255),
    AddedBy: addedBy,
  });
  return parsed.value;
}

export async function removeAllowEntry(id: string): Promise<void> {
  await deleteListItem(LISTS.emailAllowlist, id);
}
